import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, type ButtonProps, IconButton, Tooltip } from '@mui/material';

import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

interface CopyButtonProps {
  /** Text to copy to clipboard */
  value: string;
  /** Button variant */
  variant?: ButtonProps['variant'];
  /** Button size */
  size?: ButtonProps['size'];
  /** Show as icon button instead of regular button */
  iconOnly?: boolean;
  /** Custom label when not copied */
  label?: string;
  /** Custom label when copied */
  copiedLabel?: string;
  /** Tooltip text */
  tooltip?: string;
  /** Custom reset delay in milliseconds */
  resetDelay?: number;
}

/**
 * Reusable button component for copying text to clipboard with visual feedback
 *
 * @example
 * // Standard button
 * <CopyButton value={secret} />
 *
 * // Icon button
 * <CopyButton value={url} iconOnly tooltip="Copy URL" />
 *
 * // Custom styling
 * <CopyButton value={token} variant="contained" size="small" label="Copy Token" />
 */
export const CopyButton = ({
  value,
  variant = 'outlined',
  size = 'medium',
  iconOnly = false,
  label = 'Copy',
  copiedLabel = 'Copied!',
  tooltip,
  resetDelay,
}: CopyButtonProps) => {
  const { copied, copy } = useCopyToClipboard(resetDelay);

  const handleClick = () => {
    copy(value);
  };

  const buttonProps = {
    onClick: handleClick,
    size,
    sx: {
      color: copied ? '#019644' : undefined,
      borderColor: copied ? '#019644' : undefined,
      '&:hover': {
        borderColor: copied ? '#019644' : undefined,
      },
    },
  };

  if (iconOnly) {
    const iconButton = (
      <IconButton {...buttonProps}>
        {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    );

    return tooltip ? <Tooltip title={tooltip}>{iconButton}</Tooltip> : iconButton;
  }

  const button = (
    <Button
      {...buttonProps}
      variant={variant}
      startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
    >
      {copied ? copiedLabel : label}
    </Button>
  );

  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};
